import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Download, RotateCcw, Lock } from "lucide-react";
import { Locked, LockBadge } from "./Locked";
import UnlockBanner, { UnlockStickyCTA } from "./UnlockBanner";
import { trackEvent } from "../lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProfileResults({ profile, onReset }) {
  const bp = profile.blueprint;
  const ai = profile.ai_summary || {};
  const a = bp.astrology, hd = bp.human_design, gk = bp.gene_keys;
  const unlocked = !!profile.unlocked;

  const downloadPdf = () => {
    if (!unlocked) return;
    trackEvent("pdf_downloaded", { profile_id: profile.id });
    window.open(`${BACKEND_URL}/api/profile/${profile.id}/pdf`, "_blank");
  };

  // Sticky CTA — appears once the user scrolls past the inline unlock banner,
  // and hides again as soon as they scroll out of the results section.
  const [stickyVisible, setStickyVisible] = useState(false);
  useEffect(() => {
    if (unlocked) { setStickyVisible(false); return; }
    const onScroll = () => {
      const banner = document.getElementById("unlock-banner-anchor");
      const results = document.getElementById("results");
      if (!banner || !results) return;
      const b = banner.getBoundingClientRect();
      const r = results.getBoundingClientRect();
      // Visible only while we are inside the blueprint results area
      setStickyVisible(b.bottom < 0 && r.bottom > 200);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [unlocked]);

  const Section = ({ overline, title, body, testid }) => (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.6 }}
      className="glass-card rounded-2xl p-8 relative"
      data-testid={testid}
    >
      <div className="flex items-start justify-between gap-4">
        <p className="font-sans text-[10px] uppercase tracking-overline text-[#D4AF37] mb-3">{overline}</p>
        {!unlocked && <LockBadge />}
      </div>
      <h3 className="font-serif text-2xl text-white mb-4">{title}</h3>
      {unlocked ? (
        <p className="font-sans text-slate-300 leading-relaxed whitespace-pre-line reveal-blur">{body || "—"}</p>
      ) : (
        <div className="font-sans text-slate-300 leading-relaxed whitespace-pre-line">
          <span>{body || "—"}</span>
          <Locked testid={`${testid}-locked-rest`} className="mt-1 block">
            <span className="block">
              {/* Decorative filler text - blurred placeholder of paragraph length */}
              {LOREM}
            </span>
          </Locked>
        </div>
      )}
    </motion.div>
  );

  // Kerykeion returns 3-letter sign abbreviations ("Sco", "Tau"…). Expand them
  // for display so users see the full zodiac names on their unlocked chart.
  const SIGN_LABELS = {
    Ari: "Aries", Tau: "Taurus", Gem: "Gemini", Can: "Cancer",
    Leo: "Leo",   Vir: "Virgo",  Lib: "Libra",  Sco: "Scorpio",
    Sag: "Sagittarius", Cap: "Capricorn", Aqu: "Aquarius", Pis: "Pisces",
  };
  const fullSign = (s) => SIGN_LABELS[s] || s;

  const StatChip = ({ label, value, hint }) => (
    <div className="rounded-xl border border-white/5 bg-white/[0.025] p-5 relative">
      <p className="font-sans text-[10px] uppercase tracking-overline text-[#D4AF37]/80">{label}</p>
      {unlocked ? (
        <p className="mt-2 font-serif text-2xl text-white leading-tight reveal-blur">{value}</p>
      ) : (
        <p className="mt-2 font-serif text-2xl text-white leading-tight">
          <Locked>{value}</Locked>
        </p>
      )}
      {hint && (
        unlocked ? (
          <p className="mt-1 font-sans text-xs text-slate-400 reveal-blur">{hint}</p>
        ) : (
          <p className="mt-1 font-sans text-xs text-slate-400">
            <Locked>{hint}</Locked>
          </p>
        )
      )}
    </div>
  );

  return (
    <div id="results" data-testid="profile-results" className="mt-24 space-y-16">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="text-center max-w-3xl mx-auto"
      >
        <p className="font-sans text-xs tracking-overline text-[#D4AF37] mb-4">your soul blueprint</p>
        <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl text-white tracking-tight">
          <span className="italic">{bp.name}</span>
        </h2>
        <p className="mt-3 text-slate-400 font-sans text-sm">
          Born {new Date(bp.birth.date).toLocaleDateString(undefined, { year:"numeric", month:"long", day:"numeric"})} · {bp.birth.place}
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-4">
          <button
            data-testid="download-pdf-button"
            onClick={downloadPdf}
            disabled={!unlocked}
            title={unlocked ? "Download PDF" : "Unlock your blueprint to download the PDF"}
            className={`rounded-full px-7 py-3 text-xs flex items-center gap-2 ${
              unlocked
                ? "btn-gold"
                : "btn-ghost-gold cursor-not-allowed opacity-70"
            }`}
          >
            {unlocked ? <Download className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
            {unlocked ? "Download full PDF" : "PDF — locked"}
          </button>
          <button
            data-testid="reset-blueprint-button"
            onClick={onReset}
            className="btn-ghost-gold rounded-full px-7 py-3 text-xs flex items-center gap-2"
          >
            <RotateCcw className="w-4 h-4" />
            Start over
          </button>
        </div>
      </motion.div>

      {/* Bento grid: chart + big three (chart always visible — shows the wow factor) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-7 glass-card rounded-2xl p-4 md:p-6"
          data-testid="natal-chart-card"
        >
          <p className="font-sans text-[10px] uppercase tracking-overline text-[#D4AF37] mb-3 px-3">your natal birth chart</p>
          <div
            className="natal-chart-wrapper"
            dangerouslySetInnerHTML={{ __html: bp.natal_chart_svg }}
          />
        </motion.div>

        <div className="lg:col-span-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <StatChip label="Sun" value={fullSign(a.sun_sign)} hint="essence" />
          <StatChip label="Moon" value={fullSign(a.moon_sign)} hint="emotional rhythm" />
          <StatChip label="Ascendant" value={fullSign(a.ascendant.sign)} hint="how the world meets you" />
          <StatChip label="Midheaven" value={fullSign(a.midheaven.sign)} hint="vocation" />
          <StatChip label="HD Type" value={hd.type} hint={hd.strategy} />
          <StatChip label="Authority" value={hd.authority} hint="how you decide" />
          <StatChip label="Profile" value={hd.profile} hint="learning path" />
          <StatChip label="Signature" value={hd.signature} hint={`opp: ${hd.not_self}`} />
        </div>
      </div>

      {/* Unlock banner — only when locked */}
      {/* AI sections */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Section overline="who you are" title="Core essence" body={ai.core_essence} testid="ai-core-essence" />
        <Section overline="day to day" title="How you operate daily" body={ai.daily_life} testid="ai-daily" />
        <Section overline="career" title="Thriving at work" body={ai.work_life} testid="ai-work" />
        <Section overline="leverage" title="Where to direct your attention" body={ai.focus_to_succeed} testid="ai-focus" />
        <Section overline="alchemy" title="Shadow work" body={ai.shadow_work} testid="ai-shadow" />
        <Section overline="invitation" title="Your highest potential" body={ai.highest_potential} testid="ai-highest" />
      </div>

      {/* Gene Keys */}
      <div>
        <div className="text-center mb-10">
          <p className="font-sans text-[10px] uppercase tracking-overline text-[#D4AF37] mb-3">activation sequence</p>
          <h3 className="font-serif text-3xl md:text-4xl text-white">The four prime gifts</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            ["Life's Work", gk.lifes_work],
            ["Evolution", gk.evolution],
            ["Radiance", gk.radiance],
            ["Purpose", gk.purpose],
          ].map(([label, g]) => g && (
            <motion.div
              key={label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6 }}
              className="glass-card rounded-2xl p-6 relative"
              data-testid={`gk-card-${label.toLowerCase().replace(/'|\s/g, "-")}`}
            >
              <div className="flex items-start justify-between gap-3">
                <p className="font-sans text-[10px] uppercase tracking-overline text-[#D4AF37]">{label}</p>
                {!unlocked && <LockBadge />}
              </div>
              <p className="mt-3 font-serif text-2xl text-white leading-tight">{g.name}</p>
              <p className="mt-1 text-slate-400 text-sm font-sans">Gene Key {g.key}.{g.line}</p>
              <div className="mt-5 space-y-2 font-sans text-sm">
                <p>
                  <span className="text-red-300/80">Shadow:</span>{" "}
                  <span className="text-slate-300">{g.shadow}</span>
                </p>
                <p>
                  <span className="text-[#D4AF37]">Gift:</span>{" "}
                  {unlocked ? (
                    <span className="text-white reveal-blur">{g.gift}</span>
                  ) : (
                    <Locked><span className="text-white">{g.gift}</span></Locked>
                  )}
                </p>
                <p>
                  <span className="text-violet-300/80">Siddhi:</span>{" "}
                  {unlocked ? (
                    <span className="text-slate-200 italic reveal-blur">{g.siddhi}</span>
                  ) : (
                    <Locked><span className="text-slate-200 italic">{g.siddhi}</span></Locked>
                  )}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Channels (always visible, low-stakes informative) */}
      {hd.active_channels?.length > 0 && (
        <div className="glass-card rounded-2xl p-8" data-testid="channels-card">
          <p className="font-sans text-[10px] uppercase tracking-overline text-[#D4AF37] mb-3">your defined channels</p>
          <div className="flex flex-wrap gap-2">
            {hd.active_channels.map((c, i) => (
              <span key={i} className="px-3 py-1 rounded-full bg-white/5 border border-[#D4AF37]/15 text-sm text-slate-200 font-sans">
                {c.gates[0]}–{c.gates[1]} <span className="text-[#D4AF37]">{c.name}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Unlock banner — below the blurred activation sequence, above the next section */}
      {!unlocked && (
        <div id="unlock-banner-anchor">
          <UnlockBanner profileId={profile.id} stats={profile.unlock_stats} />
        </div>
      )}

      <UnlockStickyCTA profileId={profile.id} visible={stickyVisible} />
    </div>
  );
}

// Filler text used as a blurred placeholder in locked sections — pure visual,
// length-similar to a real paragraph. Never displayed legibly.
const LOREM =
  "Lorem cosmic ipsum dolor sit amet, consectetur adipiscing elit. " +
  "Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. " +
  "Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi " +
  "ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit " +
  "in voluptate velit esse cillum dolore eu fugiat nulla pariatur. " +
  "Excepteur sint occaecat cupidatat non proident, sunt in culpa.";
